'use client'
// What people bought him, drawn.
//
// Until 28 Sep a bought thing had no body in the town at all: the shop was
// shut on production partly because it would have sold chips and delivered
// nothing you could see. The models are Sam's cut of a food pack
// (public/models/buy_items.glb) and lib/items/parts.js says which pieces a
// build is and where they sit, so what lands here is the build that was
// bought: burnt onion rings on a tray with barbecue sauce, not "chips".
//
// Two places a thing can be here. Lying where it landed, a few steps from
// where he was when it was bought, until he walks up to it (the sim's
// Items.dueToBeFound); then at home by his container until he has used it
// up. The sim mirrors both onto the first roost's nest entity. (Since
// 3 Nov a thing of his can be in his bag, which shows over his head, or on
// the shelf at one of his roosts, which Hoard.jsx draws with these same
// pieces; this draws what is on neither.)
//
// Nothing loads until there is something to draw: the model is a quarter
// of a megabyte and most of the time he owns nothing.

import { Suspense, memo, useMemo } from 'react'
import * as THREE from 'three/webgpu'
import { useStoreWithEqualityFn } from 'zustand/traditional'
import useGameStore from '../../../stores/gameStore'
import { useModel } from '../hooks/useModel'
import { toNode } from '../hooks/useSceneNodeMaterials'
import { useTerrainHeight } from '../terrain/TerrainHeightProvider'
import { BUY_MODEL, BUY_SCALE, pieceKey, partsFor, DRAWN, footprintOf } from '../../../lib/items/parts'
import { HOME_NEST } from '../../../lib/things/bag'

// Home is by the first roost's container. Things stand in a row along x
// from here. (only what is on no shelf since 3 Nov: what is on one of his
// shelves is drawn on it, Hoard.jsx)
const HOME = { x: -1.3, z: 0.9 }

/**
 * The model's pieces, each standing on its own base and centred, with its
 * measured size. The export keeps Blender's Z-up turn on every node and
 * some pieces were modelled around their middle, so the measuring is done
 * here once rather than trusted from a number that goes stale on the next
 * export. Plain, so the shop's preview can prepare the same pieces without
 * a react-three canvas, and so can his bag's pictures (components/ui/
 * bagPictures.js), which draw in plain WebGL and pass their own material.
 */
export function preparePieces(scene, convert = (m, converted) => toNode(m, converted, 0.85)) {
  const converted = new Map()
  const pieces = new Map()
  for (const child of scene.children) {
    const key = pieceKey(child.userData?.name || child.name)
    if (!key) continue
    const body = child.clone(true)
    body.traverse((o) => {
      if (o.isLine || o.isLineSegments) { o.visible = false; return }
      if (!o.isMesh) return
      o.castShadow = true
      o.receiveShadow = true
      o.material = Array.isArray(o.material)
        ? o.material.map((m) => convert(m, converted))
        : convert(o.material, converted)
    })
    body.position.set(0, 0, 0)
    const holder = new THREE.Group()
    holder.add(body)
    holder.updateMatrixWorld(true)
    const box = new THREE.Box3().setFromObject(holder)
    if (box.isEmpty()) continue
    const size = box.getSize(new THREE.Vector3())
    const centre = box.getCenter(new THREE.Vector3())
    body.position.set(-centre.x, -box.min.y, -centre.z)
    pieces.set(key, { holder, size: { x: size.x, y: size.y, z: size.z } })
  }
  // Tinted copies (burnt, pale, what is poured) belong to these pieces'
  // materials, so the cache lives and dies with them.
  return { pieces, tints: new Map() }
}

// the pieces as prepared, once a model: the ground and his shelves (Hoard.jsx) draw with the same materials, not a
// converted set each (docs/world-performance.md, "Reuse a material graph that already exists")
const prepared = new WeakMap()

export function useBuyPieces() {
  const { scene } = useModel(BUY_MODEL)
  return useMemo(() => {
    if (!prepared.has(scene)) prepared.set(scene, preparePieces(scene))
    return prepared.get(scene)
  }, [scene])
}

/**
 * One build as an object, in model units under BUY_SCALE. Shared with the
 * shop's preview, which draws the same thing before it is bought.
 * `tints` caches tinted material copies so burnt chips do not make a new
 * material per chip.
 */
export function buildThing(itemId, spec, pieces, tints = new Map()) {
  const group = new THREE.Group()
  const size = (k) => pieces.get(k)?.size || { x: 0, y: 0, z: 0 }
  for (const p of partsFor(itemId, spec, size)) {
    let obj = null
    if (p.disc) {
      const key = `disc:${p.disc.color}`
      if (!tints.has(key)) tints.set(key, new THREE.MeshStandardNodeMaterial({ color: p.disc.color, roughness: 0.35 }))
      obj = new THREE.Mesh(new THREE.CircleGeometry(p.disc.radius, 8), tints.get(key))
      obj.rotation.x = -Math.PI / 2
    } else {
      const src = pieces.get(p.piece)
      // A piece the model does not have draws nothing rather than a cube:
      // a gap reads as nothing there, an error box reads as a broken shop.
      if (!src) continue
      obj = src.holder.clone(true)
      if (p.tint) {
        obj.traverse((o) => {
          if (!o.isMesh || Array.isArray(o.material)) return
          const key = `${o.material.uuid}:${p.tint.join(',')}`
          if (!tints.has(key)) {
            // Field by field, never clone(): a cloned node material came
            // back without its texture, and burnt onion rings drew as
            // flat beige, the tint and nothing under it. Same idiom as
            // toNode, for the same reason.
            const src = o.material
            const m = new THREE.MeshStandardNodeMaterial()
            m.map = src.map
            m.color = src.color.clone().multiply(new THREE.Color(...p.tint))
            m.roughness = src.roughness
            m.metalness = src.metalness
            m.side = src.side
            m.transparent = src.transparent
            m.opacity = src.opacity
            m.alphaTest = src.alphaTest
            m.vertexColors = src.vertexColors
            tints.set(key, m)
          }
          o.material = tints.get(key)
        })
      }
    }
    obj.position.set(p.x || 0, p.y || 0, p.z || 0)
    if (p.ry) obj.rotation.y = p.ry
    // Pieces from outside the pack arrive at their own sizes (parts.js).
    if (p.scale) obj.scale.setScalar(p.scale)
    group.add(obj)
  }
  group.scale.setScalar(BUY_SCALE)
  return group
}

// A turn per thing, from its id, so it does not spin on every re-render
// and a row of things does not look stamped.
function spinOf(id) {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0
  return ((h >>> 0) % 628) / 100
}

const Thing = memo(function Thing({ thing, pieces, tints, x, z, ground }) {
  const obj = useMemo(
    () => buildThing(thing.itemId, thing.spec || {}, pieces, tints),
    // The spec is small and arrives as a fresh object every broadcast;
    // its text is what actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [thing.itemId, JSON.stringify(thing.spec || {}), pieces, tints],
  )
  const y = ground ? ground.sample(x, z) : 0
  return <primitive object={obj} position={[x, Number.isFinite(y) ? y : 0, z]} rotation={[0, spinOf(thing.id), 0]} />
})

function Drawn({ things, nest }) {
  const { pieces, tints } = useBuyPieces()
  const ground = useTerrainHeight()
  const placed = []
  let along = 0
  for (const t of things) {
    if (t.state === 'waiting') {
      if (!t.pos) continue
      placed.push({ t, x: t.pos.x, z: t.pos.z })
      continue
    }
    if (!nest?.pos) continue
    const w = footprintOf(t.itemId, t.spec)
    placed.push({ t, x: nest.pos.x + HOME.x - along - w / 2, z: nest.pos.z + HOME.z })
    along += w + 0.15
  }
  return placed.map(({ t, x, z }) => (
    <Thing key={t.id} thing={t} pieces={pieces} tints={tints} x={x} z={z} ground={ground} />
  ))
}

const sameThings = (a, b) => {
  const x = a?.state?.things || []
  const y = b?.state?.things || []
  if (x.length !== y.length) return false
  for (let i = 0; i < x.length; i++) {
    if (x[i].id !== y[i].id || x[i].state !== y[i].state) return false
    if (x[i].pos?.x !== y[i].pos?.x || x[i].pos?.z !== y[i].pos?.z) return false
  }
  return a?.pos?.x === b?.pos?.x && a?.pos?.z === b?.pos?.z
}

export function BoughtThings() {
  const nest = useStoreWithEqualityFn(
    useGameStore,
    (s) => s.entityStates.find((e) => e.id === HOME_NEST) || null,
    sameThings,
  )
  const things = useMemo(() => (nest?.state?.things || []).filter((t) => DRAWN.includes(t.itemId)), [nest])
  if (!things.length) return null
  return (
    <Suspense fallback={null}>
      <Drawn things={things} nest={nest} />
    </Suspense>
  )
}

export default BoughtThings
